import { useTranslation } from 'react-i18next';
import { Carousel, CarouselContent, CarouselItem } from '../ui/carousel';
import type { Badge } from '../../types/badge';

/** dragFree drops Embla's snap points, so the strip scrolls without anchoring. */
const FREE_SCROLL = { align: 'start', dragFree: true, containScroll: 'trimSnaps' } as const;

/** One glyph per badge name (as the API sends it); anything unknown gets the medal. */
const BADGE_ICONS: Record<string, string> = {
  narcisista: '🤳',
  libere: '🧗',
  falesista: '⛰️',
  videomaker: '🎬',
  amico_mio: '🤝',
  amico_di_tutti: '🫶',
  commentatore: '💬',
  apripista: '🥇',
  tenace: '🔁',
  esploratore: '🧭',
  agonista: '🏆',
  diarista: '📓',
  sentinella: '🛡️',
  babbo_natale: '🎅',
  befana: '🧹',
  buon_anno: '🎆',
  ferragosto: '🏖️',
  halloween: '🎃',
  sesto_grado: '6️⃣',
  settimo_grado: '7️⃣',
  ottavo_grado: '8️⃣',
  lucciola: '💡',
};
const DEFAULT_BADGE_ICON = '🏅';

interface BadgesCarouselProps {
  badges: Badge[];
}

/**
 * The badges as a strip that scrolls sideways. It is meant to be rendered
 * full-bleed (the page pulls it past the gutters with `-mx-4 sm:-mx-6`), so
 * it puts the gutter back itself: the heading and the first tile line up
 * with the content above, and the tiles run off the screen edge. Embla
 * reads the leading gutter from the container's padding and the trailing
 * one from the last slide's margin, hence the two are set differently.
 */
export default function BadgesCarousel({ badges }: BadgesCarouselProps) {
  const { t } = useTranslation();

  if (badges.length === 0) return null;

  return (
    <section className="flex w-full flex-col gap-2">
      <h2 className="px-4 sm:px-6 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {t('userProfile.badges')}
      </h2>
      <Carousel opts={FREE_SCROLL} className="w-full">
        <CarouselContent className="ml-0 gap-3 pl-4 sm:pl-6">
          {badges.map((badge) => (
            <CarouselItem
              key={badge.id}
              className="basis-auto pl-0 last:mr-4 sm:last:mr-6"
            >
              <div
                className="flex h-full w-24 flex-col items-center gap-1 rounded-lg border bg-card p-2 cursor-default"
                title={badge.description}
              >
                <div
                  className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-2xl"
                  data-testid="badge-icon"
                >
                  {BADGE_ICONS[badge.name] ?? DEFAULT_BADGE_ICON}
                </div>
                <span className="text-xs font-medium text-center leading-tight">
                  {badge.badge_name}
                </span>
                {badge.level !== undefined && badge.level !== null && (
                  <span className="text-xs text-muted-foreground" data-testid="badge-level">
                    {t('userProfile.badgeLevel', { level: badge.level })}
                  </span>
                )}
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </section>
  );
}
